import { apiAgent } from "../../config/axiosConfig";
import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  updateLoadError,
  setLoadingState,
  setSavingState,
  updateTestScenariosList,
  setSelectedTestScenario,
  updateSelectedTestScenarioDetail,
  setTestScenarioTags,
} from "./testScenarioSlice";
// Constants
import { TEST_SCENARIOS_PAGE_SIZE } from "../../config/constants";

const createTestScenario = createAsyncThunk(
  "createTestScenario",
  async ({ projectId, title }, { dispatch, rejectWithValue }) => {
    await dispatch(setLoadingState(true));
    try {
      const response = await apiAgent({
        method: "POST",
        url: "/api/v1/test_scenarios/",
        data: {
          project_id: projectId,
          title: title || "",
        },
      });
      if (response.status === 200) {
        await dispatch(
          setSelectedTestScenario({ testScenarioId: response?.data?.id })
        );
        await dispatch(
          updateSelectedTestScenarioDetail({
            testScenario: { ...response?.data },
          })
        );
        await dispatch(setLoadingState(false));
        return response?.data;
      } else {
        await dispatch(setLoadingState(false));
        return rejectWithValue(response.data);
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

const getAllTestScenarios = createAsyncThunk(
  "getAllTestScenarios",
  async (
    {
      projectId,
      pageNumber,
      pageSize = TEST_SCENARIOS_PAGE_SIZE,
      searchText = null,
      tags = [],
      statuses = [],
      environmentSetId = null,
      buildStatus = null,
      ownerIds = [],
    },
    { dispatch }
  ) => {
    await dispatch(setLoadingState(true));
    try {
      const response = await apiAgent({
        method: "GET",
        url: "/api/v1/test_scenarios",
        params: {
          page: pageNumber,
          page_size: pageSize,
          project_id: projectId,
          search_text: searchText,
          environment_set_id: environmentSetId,
          build_status: buildStatus,
          owner_ids: ownerIds,
          tags,
          statuses,
        },
      });
      if (response.status === 200) {
        await dispatch(
          updateTestScenariosList({
            testScenariosList: response?.data?.test_scenarios,
            totalPages: response?.data?.total_pages,
            total: response?.data?.total,
            scenario_being_generated: response?.data?.scenario_being_generated,
          })
        );
        await dispatch(setLoadingState(false));
        return response?.data;
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
      await dispatch(setLoadingState(false));
    }
  }
);

const updateTestScenario = createAsyncThunk(
  "updateTestScenario",
  async ({ testScenarioId, data }, { dispatch }) => {
    await dispatch(setSavingState(true));
    try {
      const response = await apiAgent({
        method: "PUT",
        url: `/api/v1/test_scenarios/${testScenarioId}`,
        data,
      });
      if (response.status === 200) {
        await dispatch(
          updateSelectedTestScenarioDetail({
            testScenarioDetail: { ...response?.data },
          })
        );
        await dispatch(setSavingState(false));
        return response?.data;
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

const fetchTestScenarioDetail = createAsyncThunk(
  "fetchTestScenarioDetail",
  async ({ testScenarioId, projectId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await apiAgent({
        method: "GET",
        url: `/api/v1/test_scenarios/${testScenarioId}`,
        // params: {
        //   project_id: projectId,
        // },
      });
      if (response.status === 200) {
        await dispatch(
          updateSelectedTestScenarioDetail({
            testScenarioDetail: { ...response?.data },
          })
        );
        return response?.data;
      }
    } catch (err) {
      // If the test scenario does not exist, set the test scenario detail to an empty object
      await dispatch(
        updateSelectedTestScenarioDetail({
          testScenarioDetail: {},
        })
      );
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

const deleteTestScenario = createAsyncThunk(
  "deleteTestScenario",
  async ({ testScenarioId }, { dispatch, rejectWithValue }) => {
    await dispatch(setSavingState(true));
    try {
      const response = await apiAgent({
        method: "DELETE",
        url: `/api/v1/test_scenarios/${testScenarioId}`,
      });
      if (response.status === 204) {
        await dispatch(setSavingState(false));
        return response?.data;
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

const cloneTestScenario = createAsyncThunk(
  "cloneTestScenario",
  async ({ testScenarioId }, { dispatch, rejectWithValue }) => {
    await dispatch(setLoadingState(true));
    try {
      const response = await apiAgent({
        method: "POST",
        url: `/api/v1/test_scenarios/${testScenarioId}/clone`,
      });
      if (response.status === 200) {
        await dispatch(
          setSelectedTestScenario({ testScenarioId: response?.data?.id })
        );
        // await dispatch(setLoadingState(false));
        return response?.data;
      } else {
        await dispatch(setLoadingState(false));
        return rejectWithValue(response.data);
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

const getTestScenarioTags = createAsyncThunk(
  "getTestScenarioTags",
  async ({ projectId }, { dispatch }) => {
    try {
      const response = await apiAgent({
        method: "GET",
        url: "/api/v1/test_scenarios/tags",
        params: {
          project_id: projectId,
        },
      });
      if (response.status === 200) {
        await dispatch(setTestScenarioTags(response.data.tags));
        return response.data.tags;
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

const exportDetailed = createAsyncThunk(
  "exportDetailed",
  async (
    {
      projectId,
      searchText = null,
      tags = [],
      statuses = [],
      environmentSetId = null,
      buildStatus = null,
      ownerIds = [],
    },
    { dispatch, rejectWithValue }
  ) => {
    await dispatch(setLoadingState(true));
    try {
      const response = await apiAgent({
        method: "POST",
        url: "/api/v1/test_scenarios/export_detailed",
        data: {
          project_id: projectId,
          search_text: searchText,
          environment_set_id: environmentSetId,
          build_status: buildStatus,
          owner_ids: ownerIds,
          tags,
          statuses,
        },
      });
      if (response.status === 200) {
        await dispatch(setLoadingState(false));
        return response?.data;
      } else {
        await dispatch(setLoadingState(false));
        return rejectWithValue(response.data);
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

const exportBrief = createAsyncThunk(
  "exportBrief",
  async (
    {
      projectId,
      searchText = null,
      tags = [],
      statuses = [],
      environmentSetId = null,
      buildStatus = null,
      ownerIds = [],
    },
    { dispatch, rejectWithValue }
  ) => {
    await dispatch(setLoadingState(true));
    try {
      const response = await apiAgent({
        method: "POST",
        url: "/api/v1/test_scenarios/export_brief",
        data: {
          project_id: projectId,
          search_text: searchText,
          environment_set_id: environmentSetId,
          build_status: buildStatus,
          owner_ids: ownerIds,
          tags,
          statuses,
        },
      });
      if (response.status === 200) {
        await dispatch(setLoadingState(false));
        return response?.data;
      } else {
        await dispatch(setLoadingState(false));
        return rejectWithValue(response.data);
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

const bulkDeleteTestScenarios = createAsyncThunk(
  "bulkDeleteTestScenarios",
  async ({ projectId, testScenarioIds }, { dispatch, rejectWithValue }) => {
    await dispatch(setSavingState(true));
    try {
      const response = await apiAgent({
        method: "DELETE",
        url: "/api/v1/test_scenarios/bulk_destroy",
        data: {
          project_id: projectId,
          test_scenario_ids: testScenarioIds,
        },
      });

      // Wait for the loading state to be set
      await dispatch(setSavingState(false));

      // Check for successful response
      if (response.status === 204 || response.status === 200) {
        return { success: true };
      } else {
        return rejectWithValue({ message: "Failed to delete test scenarios" });
      }
    } catch (err) {
      await dispatch(setSavingState(false));
      return rejectWithValue(
        err.response?.data || { message: "Failed to delete test scenarios" }
      );
    }
  }
);

const fetchTestScenarioDataFiles = createAsyncThunk(
  "fetchTestScenarioDataFiles",
  async ({ testScenarioId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await apiAgent({
        method: "GET",
        url: "/api/v1/test_scenario_data_files",
        params: {
          test_scenario_id: testScenarioId,
        },
      });
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { message: "Failed to fetch data files" }
      );
    }
  }
);

const addTestScenarioDataFile = createAsyncThunk(
  "addTestScenarioDataFile",
  async (
    { testScenarioId, environmentSetId, dataFileId },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const response = await apiAgent({
        method: "POST",
        url: "/api/v1/test_scenario_data_files",
        data: {
          test_scenario_id: testScenarioId,
          environment_set_id: environmentSetId,
          data_file_id: dataFileId,
        },
      });
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { message: "Failed to add data file" }
      );
    }
  }
);

const updateTestScenarioDataFile = createAsyncThunk(
  "updateTestScenarioDataFile",
  async (
    { testScenarioDataFileId, selected },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const response = await apiAgent({
        method: "PUT",
        url: `/api/v1/test_scenario_data_files/${testScenarioDataFileId}`,
        data: {
          selected,
        },
      });
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { message: "Failed to update data file" }
      );
    }
  }
);

const deleteTestScenarioDataFile = createAsyncThunk(
  "deleteTestScenarioDataFile",
  async ({ testScenarioDataFileId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await apiAgent({
        method: "DELETE",
        url: `/api/v1/test_scenario_data_files/${testScenarioDataFileId}`,
      });
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { message: "Failed to delete data file" }
      );
    }
  }
);

const fetchTestScenarioCode = createAsyncThunk(
  "fetchTestScenarioCode",
  async (
    { testScenarioId, environmentSetId },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const response = await apiAgent({
        method: "GET",
        url: `/api/v1/test_scenarios/${testScenarioId}/code`,
        params: {
          environment_set_id: environmentSetId,
        },
      });
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { message: "Failed to fetch data files" }
      );
    }
  }
);

const syncTestScenariosToGitHub = createAsyncThunk(
  "syncTestScenariosToGitHub",
  async ({ projectId, commitMessage }, { dispatch, rejectWithValue }) => {
    try {
      const response = await apiAgent({
        method: "POST",
        url: `/api/v1/test_scenarios/sync_to_github`,
        data: {
          project_id: projectId,
          commit_message: commitMessage,
        },
      });
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

const inviteGitHubUser = createAsyncThunk(
  "inviteGitHubUser",
  async ({ projectId, gitHubUserName }, { dispatch, rejectWithValue }) => {
    try {
      const response = await apiAgent({
        method: "POST",
        url: `/api/v1/test_scenarios/invite_to_github`,
        data: {
          project_id: projectId,
          github_username: gitHubUserName,
        },
      });
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

const updateTestScenarioYMLCode = createAsyncThunk(
  "updateTestScenarioYMLCode",
  async ({ testScenarioId, code }, { dispatch, rejectWithValue }) => {
    try {
      const response = await apiAgent({
        method: "PUT",
        url: `/api/v1/test_scenarios/${testScenarioId}/update_via_yml`,
        data: {
          yml_content: code,
        },
      });
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

const fetchSearchedTestScenarios = createAsyncThunk(
  "fetchSearchedTestScenarios",
  async ({ projectId, searchText = null }, { dispatch }) => {
    await dispatch(setLoadingState(true));
    try {
      const response = await apiAgent({
        method: "GET",
        url: "/api/v1/test_scenarios",
        params: {
          project_id: projectId,
          search_text: searchText,
          statuses: ["active"],
        },
      });
      if (response.status === 200) {
        await dispatch(setLoadingState(false));
        return response?.data;
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
      await dispatch(setLoadingState(false));
    }
  }
);

export {
  createTestScenario,
  getAllTestScenarios,
  updateTestScenario,
  fetchTestScenarioDetail,
  deleteTestScenario,
  cloneTestScenario,
  getTestScenarioTags,
  exportBrief,
  exportDetailed,
  bulkDeleteTestScenarios,
  fetchTestScenarioDataFiles,
  addTestScenarioDataFile,
  updateTestScenarioDataFile,
  deleteTestScenarioDataFile,
  fetchTestScenarioCode,
  syncTestScenariosToGitHub,
  inviteGitHubUser,
  updateTestScenarioYMLCode,
  fetchSearchedTestScenarios,
};
